import { useState } from 'react'
import { ImageIcon } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ChannelToggle, type Channel } from '../components/ChannelToggle'
import { VideoGuidePanel, type VideoGuide } from '../components/VideoGuidePanel'

const SOCIAL_STEPS = [
  {
    step: 1,
    title: 'Localise Assets in FordShare',
    items: [
      <>Review the rows assigned to you in the Content Tracker</>,
      <>Localise your assets in FordShare</>,
      <>Once assets are finalised, return to Content Tracker</>,
    ],
    note: null,
  },
  {
    step: 2,
    title: 'Add the Activity ID',
    items: [
      <>Open the relevant row in Content Tracker</>,
      <>Enter the Activity ID(s) from FordShare</>,
      <>This automatically changes the row status to <strong className="text-slate-800">Asset in Development</strong></>,
      <>When you have inserted your FordShare Activity IDs, the Content Tracker will now search for relevant files for that ID in GEAR Exchange</>,
      <>From the Asset Overlay, you can directly select which assets are relevant for your campaign. You'll be shown if an asset has already been used in a previous variant. Use the 'Best Match' feature — Content Tracker will analyse your campaign fields and suggest the most suitable</>,
      <>Alternatively, upload your images (click Save to save images only, without running validations)</>,
      <>Once you click Save, the status will update to <strong className="text-slate-800">Ad Copy Creation</strong></>,
    ],
    note: null,
  },
  {
    step: 3,
    title: 'Create Ad Copy',
    items: [
      <>When status = Ad Copy Creation, open the row and click <strong className="text-slate-800">Asset Overlay</strong></>,
      <>Enter all text copy fields</>,
      <>Click <strong className="text-slate-800">Save & Validate</strong></>,
    ],
    note: 'All validation errors must be resolved before you can approve. You cannot proceed with errors outstanding.',
  },
  {
    step: 4,
    title: 'Approve the Bundle',
    items: [
      <>Once all validation errors are cleared, click <strong className="text-slate-800">Approve Bundle</strong></>,
      <>Status changes to <strong className="text-slate-800">Activation in Setup</strong></>,
      <>Activation Team is notified automatically — your process ends here</>,
    ],
    note: null,
  },
]

const SEARCH_STEPS = [
  {
    step: 1,
    title: 'Localise Assets in FordShare',
    items: [
      <>Review the rows assigned to you in the Content Tracker</>,
      <>Localise your assets in FordShare</>,
      <>Once assets are finalised, return to Content Tracker</>,
    ],
    note: null,
  },
  {
    step: 2,
    title: 'Add the Activity ID',
    items: [
      <>Open the relevant row in Content Tracker</>,
      <>Enter the Activity ID(s) from FordShare</>,
      <>This automatically changes the row status to <strong className="text-slate-800">Asset in Development</strong></>,
      <>When you have inserted your FordShare Activity IDs, the Content Tracker will now search for relevant files for that ID in GEAR Exchange</>,
      <>From the Asset Overlay, you can directly select which assets are relevant for your campaign. You'll be shown if an asset has already been used in a previous variant. Use the 'Best Match' feature — Content Tracker will analyse your campaign fields and suggest the most suitable</>,
      <>Alternatively, upload your images (click Save to save images only, without running validations)</>,
      <>Once you click Save, the status will update to <strong className="text-slate-800">Ad Copy Creation</strong></>,
    ],
    note: null,
  },
  {
    step: 3,
    title: 'Create Ad Copy *,**',
    items: [
      <>When status = Ad Copy Creation, open the row and click <strong className="text-slate-800">Asset Overlay</strong></>,
      <>Enter all text copy fields</>,
      <>Click <strong className="text-slate-800">Save & Validate</strong></>,
    ],
    note: 'All validation errors must be resolved before you can approve. You cannot proceed with errors outstanding.',
  },
  {
    step: 4,
    title: 'Approve the Bundle',
    items: [
      <>Once all validation errors are cleared, click <strong className="text-slate-800">Approve Bundle</strong></>,
      <>Status changes to <strong className="text-slate-800">Activation in Setup</strong></>,
      <>Activation Team is notified automatically — your process ends here</>,
    ],
    note: null,
  },
]

const SEARCH_LIVE_STEPS = [
  {
    step: 1,
    title: 'Locking Logic',
    items: [
      <>When a campaign goes live, the current row/version is locked, and no further edits are allowed</>,
    ],
  },
  {
    step: 2,
    title: 'Create a New Version',
    items: [
      <>Users can create a new version directly in the asset section by clicking <strong className="text-slate-800">"Create New Version"</strong></>,
      <>This will duplicate the latest live version</>,
    ],
  },
  {
    step: 3,
    title: 'Update Assets',
    items: [
      <>Update assets in accordance with the standard process</>,
      <>Remember to upload the new assets to the <strong className="text-slate-800">Finalized folder on FordShare</strong></>,
    ],
  },
  {
    step: 4,
    title: 'Approve in FordShare',
    items: [
      <>Once the visual(s) are approved in FordShare, open the Asset Modal for the campaign in question in Content Tracker (referencing the Campaign ID)</>,
    ],
  },
  {
    step: 5,
    title: 'Update in Content Tracker',
    items: [
      <>Update the campaign visuals in accordance with the brief</>,
      <>Scroll to the <strong className="text-slate-800">Asset Feedback</strong> section and reference all the updates you have made (e.g. "I deleted image 2 and uploaded a new image 4")</>,
      <>Click <strong className="text-slate-800">Save & Validate</strong></>,
    ],
  },
  {
    step: 6,
    title: 'Notify the Planner',
    items: [
      <>Once all errors and warnings have been cleared, tag the responsible planner in the <strong className="text-slate-800">Asset Feedback</strong> section</>,
      <>Confirm that the campaign is ready for update (referencing the Campaign ID)</>,
    ],
  },
]

const VIDEOS: VideoGuide[] = [
  { title: 'Localizing Assets', description: 'FordShare localisation workflow', src: '/api/static/content-tracker/about-videos/Localizing Assets.mp4', channel: 'both' },
  { title: 'Adding Assets in Content Modal', description: 'Images & ad copy walkthrough', src: '/api/static/content-tracker/about-videos/How to add assets in the Content Modal (images & ad copy).mp4', channel: 'both' },
  { title: 'GEAR Exchange Training', description: 'Import assets from GX', src: '/api/static/content-tracker/about-videos/training GX.mp4', channel: 'both' },
  { title: 'Archiving Campaigns', description: 'Managing your working view', src: '/api/static/content-tracker/about-videos/Archiving Campaigns .mp4', channel: 'both' },
]

function StepList({ steps, color }: { steps: { step: number; title: string; items: React.ReactNode[]; note?: string | null }[]; color: string }) {
  return (
    <div className="space-y-0">
      {steps.map(({ step, title, items, note }, i, arr) => (
        <div key={step} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full bg-${color}-100 text-${color}-700 text-xs font-bold flex items-center justify-center shrink-0 z-10`}>
              {step}
            </div>
            {i < arr.length - 1 && (
              <div className="w-px flex-1 bg-slate-200 my-1.5" />
            )}
          </div>
          <div className={`min-w-0 flex-1 ${i < arr.length - 1 ? 'pb-6' : ''}`}>
            <p className="text-sm font-semibold text-slate-800 mt-0.5 mb-2">{title}</p>
            <ul className="space-y-1.5">
              {items.map((item, j) => (
                <li key={j} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className={`text-${color}-300 shrink-0 mt-0.5`}>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {note && (
              <div className={`mt-3 border-l-[3px] border-${color}-400 pl-3 py-0.5`}>
                <p className={`text-sm text-${color}-800 leading-relaxed`}>{note}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function SocialContent() {
  return (
    <>
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Process</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Your 4-step process</CardTitle>
        </CardHeader>
        <CardContent>
          <StepList steps={SOCIAL_STEPS} color="purple" />
        </CardContent>
      </Card>

      {/* Tier & Buy-Type */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">KPIs</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">KPIs</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            Tier and buy-type define the purpose of each row and are visible in the "Buy Type" and "Tier" columns respectively.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-3">
            <Card className="shadow-none bg-slate-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Buy-Type</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {[
                    <><strong className="text-slate-700">CPM</strong> = Awareness</>,
                    <><strong className="text-slate-700">CPC</strong> = Consideration</>,
                    <><strong className="text-slate-700">CPL</strong> = Lead Gen</>,
                  ].map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                      <span className="text-purple-300 shrink-0 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="shadow-none bg-slate-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Tier</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {[
                    <><strong className="text-slate-700">Tier 1</strong> (Brand)</>,
                    <><strong className="text-slate-700">Tier 1.5</strong> (Consideration)</>,
                    <><strong className="text-slate-700">Tier 2</strong> (Lead Gen)</>,
                  ].map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                      <span className="text-purple-300 shrink-0 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Live Updates */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Live updates</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Making updates to live campaigns</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            If a client requests updates of visuals for a live campaign, you will be notified outside the system, as you do today. The client will reference the Campaign ID.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StepList steps={[
            { step: 1, title: 'Update Assets', items: [<>Update assets in accordance with the standard process</>, <>Remember to upload the new assets to the <strong className="text-slate-800">Finalized folder on FordShare</strong></>] },
            { step: 2, title: 'Approve in FordShare', items: [<>Once the visual(s) are approved in FordShare, open the Asset Modal for the campaign in question in Content Tracker (referencing the Campaign ID)</>] },
            { step: 3, title: 'Update in Content Tracker', items: [<>Update the campaign visuals in accordance with the brief</>, <>Scroll to the <strong className="text-slate-800">Asset Feedback</strong> section and reference all the updates you have made</>, <>Click <strong className="text-slate-800">Save & Validate</strong></>] },
            { step: 4, title: 'Notify the Planner', items: [<>Once all errors and warnings have been cleared, tag the responsible planner in the <strong className="text-slate-800">Asset Feedback</strong> section</>, <>Confirm that the campaign is ready for update (referencing the Campaign ID)</>] },
          ]} color="purple" />
        </CardContent>
      </Card>

      {/* Archiving */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Archive</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Archiving rows</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            An optional tool to keep your working view clean and suppress unnecessary notifications. It does not delete any data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-purple-600 mb-2.5">When to archive</p>
            <ul className="space-y-2">
              {['Assets will not be received and delivery is confirmed as not happening', 'A format or placement has been confirmed as not required', 'A row should not be progressed at this time'].map((item) => (
                <li key={item} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className="text-purple-300 shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t border-slate-100 pt-5">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-purple-600 mb-2.5">How to archive / unarchive</p>
            <ul className="space-y-2">
              {[
                <>Open the row → click <strong className="text-slate-700">Status Overlay</strong> → scroll down and click <strong className="text-slate-700">Archive</strong></>,
                <>To unarchive: apply filter <strong className="text-slate-700">"Show archived rows"</strong> → open row → click <strong className="text-slate-700">Unarchive</strong></>,
              ].map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className="text-purple-300 shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

function SearchContent() {
  return (
    <>
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Process</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Your 4-step process</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <StepList steps={SEARCH_STEPS} color="purple" />
          <div className="border-t border-slate-100 pt-4 space-y-1.5">
            <p className="text-xs text-slate-400 italic">* Search campaigns will skip statuses Waiting for Client Brief and Asset in Production, as no imagery is needed for RSA campaigns.</p>
            <p className="text-xs text-slate-400 italic">** For ESO-markets, the local approvers are responsible for creating ad copy, and they will be notified via email.</p>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">KPIs</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">KPIs</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            Tier and buy-type define the purpose of each row and are visible in the "Buy Type" and "Tier" columns respectively.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-3">
            <Card className="shadow-none bg-slate-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Buy-Type</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {[
                    <><strong className="text-slate-700">CPM</strong> = Awareness</>,
                    <><strong className="text-slate-700">CPC</strong> = Consideration</>,
                    <><strong className="text-slate-700">CPL</strong> = Lead Gen</>,
                  ].map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                      <span className="text-purple-300 shrink-0 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="shadow-none bg-slate-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Tier</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {[
                    <><strong className="text-slate-700">Tier 1</strong> (Brand)</>,
                    <><strong className="text-slate-700">Tier 1.5</strong> (Consideration)</>,
                    <><strong className="text-slate-700">Tier 2</strong> (Lead Gen)</>,
                  ].map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                      <span className="text-purple-300 shrink-0 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Live Updates */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Live updates</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Making updates to live campaigns</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            If a client requests updates of visuals for a live campaign, you will be notified outside the system, as you do today. The client will reference the Campaign ID.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StepList steps={SEARCH_LIVE_STEPS} color="purple" />
        </CardContent>
      </Card>

      {/* Archiving */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Archive</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Archiving rows</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            An optional tool to keep your working view clean and suppress unnecessary notifications. It does not delete any data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-purple-600 mb-2.5">When to archive</p>
            <ul className="space-y-2">
              {['Assets will not be received and delivery is confirmed as not happening', 'A format or placement has been confirmed as not required', 'A row should not be progressed at this time'].map((item) => (
                <li key={item} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className="text-purple-300 shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t border-slate-100 pt-5">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-purple-600 mb-2.5">How to archive / unarchive</p>
            <ul className="space-y-2">
              {[
                <>Open the row → click <strong className="text-slate-700">Status Overlay</strong> → scroll down and click <strong className="text-slate-700">Archive</strong></>,
                <>To unarchive: apply filter <strong className="text-slate-700">"Show archived rows"</strong> → open row → click <strong className="text-slate-700">Unarchive</strong></>,
              ].map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className="text-purple-300 shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

export function CreativeAgencyTab() {
  const [channel, setChannel] = useState<Channel>('social');

  return (
    <div className="flex gap-8 max-w-6xl mx-auto">
      <div className="flex-1 min-w-0 space-y-6">
        <ChannelToggle value={channel} onChange={setChannel} color="purple" />

        <Card className="shadow-none border-slate-300">
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Creative Agency</p>
          </div>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                <ImageIcon className="w-5 h-5 text-purple-600" strokeWidth={1.75} />
              </div>
              <CardTitle className="text-xl">Creative Agency Guidelines</CardTitle>
            </div>
            <CardDescription className="leading-relaxed mt-1">
              Localise assets, write ad copy, validate content, and approve bundles for activation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-l-[3px] border-purple-400 pl-4 py-0.5">
              <p className="text-sm text-slate-700 leading-relaxed">
                Your job is to deliver localised, platform-ready, and validated creative assets that are fully compliant and ready for activation.
              </p>
            </div>
            <div className="border-l-[3px] border-purple-300 pl-4 py-0.5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-purple-600 mb-1">Starts when</p>
              <p className="text-sm text-slate-700 leading-relaxed">
                You are notified via Teams when rows are ready. Look for rows with status: <strong className="text-slate-800">Waiting for Client Brief / Asset in Development</strong>
              </p>
            </div>
            <div className="border-l-[3px] border-purple-300 pl-4 py-0.5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-purple-600 mb-1">SLA reminder</p>
              <p className="text-sm text-slate-700 leading-relaxed">
                SLAs for asset delivery do not change and should be <strong className="text-slate-800">11 days before the campaign go-live date</strong>.
              </p>
            </div>
            <div className="border-l-[3px] border-purple-300 pl-4 py-0.5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-purple-600 mb-1">Client ad copy</p>
              <p className="text-sm text-slate-700 leading-relaxed">
                For the ESO markets, clients are adding copy directly into the tool. You will also get a notification when this needs to be done via email.
              </p>
            </div>
          </CardContent>
        </Card>

        {channel === 'social' ? <SocialContent /> : <SearchContent />}
      </div>

      <div className="w-64 flex-shrink-0 sticky top-20 self-start">
        <VideoGuidePanel videos={VIDEOS} />
      </div>
    </div>
  )
}
